import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

import FeatherIcon from "feather-icons-react";

import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

const formatDate = (value) => {
  if (!value) return "-";

  const text = String(value).trim();

  const dayFirstMatch = text.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/
  );

  if (dayFirstMatch) {
    const [, day, month, year] = dayFirstMatch;
    return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
  }

  const yearFirstMatch = text.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})/
  );

  if (yearFirstMatch) {
    const [, year, month, day] = yearFirstMatch;
    return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
  }

  return text;
};

// Normalize supported date formats to YYYY-MM-DD for reliable comparisons.
const toISODate = (value) => {
  if (!value) return "";

  const text = String(value).trim();

  const isoMatch = text.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})/
  );

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  const dayFirstMatch = text.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/
  );

  if (dayFirstMatch) {
    const [, day, month, year] = dayFirstMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return "";
};

const getTodayISO = () => {
  const date = new Date();

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

// Defaults to the same calendar day in the previous month, clamped for short months.
const getOneMonthAgoISO = () => {
  const today = new Date();
  const targetDay = today.getDate();

  today.setDate(1);
  today.setMonth(today.getMonth() - 1);

  const lastDay = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  ).getDate();

  today.setDate(Math.min(targetDay, lastDay));

  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
};

const formatEmployeeName = (value) => {
  if (!value) return "-";

  return String(value)
    .trim()
    .replace(/[._,]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatPunchType = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();

  switch (normalized) {
    case "normal":
      return "Normal";
    case "lunch":
      return "Lunch";
    case "business":
      return "Business";
    case "personalbusiness":
    case "personal business":
      return "Personal Business";
    default:
      return value || "-";
  }
};

const normalizePunchFor = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();

  if (normalized === "in") return "In";
  if (normalized === "out") return "Out";

  return normalized;
};

const normalizePunchType = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();

  if (normalized === "personalbusiness" || normalized === "personal business") {
    return "personalbusiness";
  }

  return normalized;
};

const MissingTable = ({
  missingPunchRecords = [],
  onEdit,
  selectedMissingPunchId = 0,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const [filters, setFilters] = useState(() => ({
    employeeId: "",
    employeeName: "",
    organization: "",
    fromDate: getOneMonthAgoISO(),
    toDate: getTodayISO(),
    punchType: "",
    status: "",
    missingPunch: "",
    time: "",
  }));

  const records = Array.isArray(missingPunchRecords)
    ? missingPunchRecords
    : [];

  const handleFilterChange = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      employeeId: "",
      employeeName: "",
      organization: "",
      fromDate: getOneMonthAgoISO(),
      toDate: getTodayISO(),
      punchType: "",
      status: "",
      missingPunch: "",
      time: "",
    });
    setPage(0);
  };

  const filteredRecords = useMemo(() => {
    const employeeIdFilter = filters.employeeId.trim().toLowerCase();
    const employeeNameFilter = filters.employeeName
      .trim()
      .replace(/[._,]+/g, " ")
      .replace(/\s+/g, " ")
      .toLowerCase();

    return records.filter((row) => {
      const employeeId = String(row.employeeId ?? "").toLowerCase();

      const employeeName = String(row.personName ?? "")
        .replace(/[._,]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      const organization = String(row.organization ?? "")
        .trim()
        .toLowerCase();

      const organizationFilter = filters.organization.trim().toLowerCase();

      const recordDate = toISODate(row.missingDate);
      const punchType = normalizePunchType(row.punchType);
      const status = String(row.attendanceStatus ?? "")
        .trim()
        .toUpperCase();
      const punchFor = normalizePunchFor(row.punchFor);

      const matchesEmployeeId =
        !employeeIdFilter || employeeId.includes(employeeIdFilter);

      const matchesEmployeeName =
        !employeeNameFilter || employeeName.includes(employeeNameFilter);

      const matchesOrganization =
        !organizationFilter || organization.includes(organizationFilter);

      const matchesFromDate =
        !filters.fromDate ||
        (recordDate && recordDate >= filters.fromDate);

      const matchesToDate =
        !filters.toDate ||
        (recordDate && recordDate <= filters.toDate);

      const matchesPunchType =
        !filters.punchType || punchType === filters.punchType;

      const matchesStatus =
        !filters.status || status === filters.status;

      const matchesMissingPunch =
        !filters.missingPunch || punchFor === filters.missingPunch;

      const timeValue = String(row.time ?? "").toLowerCase();
      const matchesTime =
        !filters.time.trim() ||
        timeValue.includes(filters.time.trim().toLowerCase());

      return (
        matchesEmployeeId &&
        matchesEmployeeName &&
        matchesOrganization &&
        matchesFromDate &&
        matchesToDate &&
        matchesPunchType &&
        matchesStatus &&
        matchesMissingPunch &&
        matchesTime
      );
    });
  }, [records, filters]);

  useEffect(() => {
    const maximumPage =
      rowsPerPage > 0
        ? Math.max(0, Math.ceil(filteredRecords.length / rowsPerPage) - 1)
        : 0;

    if (page > maximumPage) {
      setPage(maximumPage);
    }
  }, [filteredRecords.length, page, rowsPerPage]);

  const visibleRecords = useMemo(() => {
    if (rowsPerPage === -1) return filteredRecords;

    const startIndex = page * rowsPerPage;
    return filteredRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRecords, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {/* Filter toolbar */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant={showColumnFilters ? "contained" : "outlined"}
              color="primary"
              size="small"
              onClick={() => setShowColumnFilters((previous) => !previous)}
              startIcon={<FeatherIcon icon="filter" size={16} />}
              aria-expanded={showColumnFilters}
              sx={{ textTransform: "none" }}
            >
              {showColumnFilters ? "Hide Filters" : "Show Filters"}
            </Button>

            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${filteredRecords.length} records`}
            />
          </Box>

          {showColumnFilters && (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={clearFilters}
              startIcon={<FeatherIcon icon="x-circle" size={15} />}
              sx={{ textTransform: "none" }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box>

      {/* Records table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 450,
          borderRadius: 0,
          boxShadow: "none",
          overflowX: "auto",
        }}
      >
        <Table
          stickyHeader
          aria-label="missing punch records"
          sx={{ minWidth: 1400, whiteSpace: "nowrap", tableLayout: "auto" }}
        >
          <TableHead>
            {/* Column headings */}
            <TableRow>
              <TableCell><strong>Employee ID</strong></TableCell>
              <TableCell><strong>Employee Name</strong></TableCell>
              <TableCell><strong>Organization</strong></TableCell>
              <TableCell><strong>Missing Date</strong></TableCell>
              <TableCell><strong>Missing Punch</strong></TableCell>
              <TableCell><strong>Time</strong></TableCell>
              <TableCell><strong>Punch Type</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Action</strong></TableCell>
            </TableRow>

            {showColumnFilters && (
              <>
                {/* Filters aligned under each column heading */}
                <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  bgcolor: "background.paper",
                  py: 1,
                  minWidth: 110,
                  verticalAlign: "top",
                },
              }}
            >
              <TableCell>
                <CustomTextField
                  fullWidth
                  size="small"
                  placeholder="Search ID"
                  value={filters.employeeId}
                  onChange={(event) =>
                    handleFilterChange("employeeId", event.target.value)
                  }
                  inputProps={{ "aria-label": "Filter employee ID" }}
                />
              </TableCell>

              <TableCell sx={{ minWidth: 180 }}>
                <CustomTextField
                  fullWidth
                  size="small"
                  placeholder="Search name"
                  value={filters.employeeName}
                  onChange={(event) =>
                    handleFilterChange("employeeName", event.target.value)
                  }
                  inputProps={{ "aria-label": "Filter employee name" }}
                />
              </TableCell>

              <TableCell sx={{ minWidth: 170 }}>
                <CustomTextField
                  fullWidth
                  size="small"
                  placeholder="Search organization"
                  value={filters.organization}
                  onChange={(event) =>
                    handleFilterChange("organization", event.target.value)
                  }
                  inputProps={{ "aria-label": "Filter organization" }}
                />
              </TableCell>

              <TableCell
                sx={{
                  width: 235,
                  minWidth: 235,
                  verticalAlign: "top",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <CustomTextField
                    fullWidth
                    size="small"
                    type="date"
                    value={filters.fromDate}
                    onChange={(event) =>
                      handleFilterChange("fromDate", event.target.value)
                    }
                    inputProps={{
                      max: filters.toDate || undefined,
                      "aria-label": "Filter missing date from",
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      "& .MuiInputBase-root": { width: "100%" },
                      "& input": { minWidth: 0, boxSizing: "border-box" },
                    }}
                  />
                  <CustomTextField
                    fullWidth
                    size="small"
                    type="date"
                    value={filters.toDate}
                    onChange={(event) =>
                      handleFilterChange("toDate", event.target.value)
                    }
                    inputProps={{
                      min: filters.fromDate || undefined,
                      "aria-label": "Filter missing date to",
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      "& .MuiInputBase-root": { width: "100%" },
                      "& input": { minWidth: 0, boxSizing: "border-box" },
                    }}
                  />
                </Box>
              </TableCell>

              <TableCell sx={{ minWidth: 165 }}>
                <CustomTextField
                  select
                  fullWidth
                  size="small"
                  value={filters.missingPunch}
                  onChange={(event) =>
                    handleFilterChange("missingPunch", event.target.value)
                  }
                  SelectProps={{ displayEmpty: true }}
                  inputProps={{ "aria-label": "Filter missing punch" }}
                >
                  <MenuItem value="">All Punches</MenuItem>
                  <MenuItem value="In">Punch In Missing</MenuItem>
                  <MenuItem value="Out">Punch Out Missing</MenuItem>
                </CustomTextField>
              </TableCell>

              <TableCell sx={{ minWidth: 120 }}>
                <CustomTextField
                  fullWidth
                  size="small"
                  placeholder="Search time"
                  value={filters.time}
                  onChange={(event) =>
                    handleFilterChange("time", event.target.value)
                  }
                  inputProps={{ "aria-label": "Filter time" }}
                />
              </TableCell>

              <TableCell sx={{ minWidth: 140 }}>
                <CustomTextField
                  select
                  fullWidth
                  size="small"
                  value={filters.punchType}
                  onChange={(event) =>
                    handleFilterChange("punchType", event.target.value)
                  }
                  SelectProps={{ displayEmpty: true }}
                  inputProps={{ "aria-label": "Filter punch type" }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="lunch">Lunch</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="personalbusiness">Personal Business</MenuItem>
                </CustomTextField>
              </TableCell>

              <TableCell sx={{ minWidth: 120 }}>
                <CustomTextField
                  select
                  fullWidth
                  size="small"
                  value={filters.status}
                  onChange={(event) =>
                    handleFilterChange("status", event.target.value)
                  }
                  SelectProps={{ displayEmpty: true }}
                  inputProps={{ "aria-label": "Filter status" }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="M">M - Missing</MenuItem>
                </CustomTextField>
              </TableCell>

              <TableCell />
                </TableRow>
              </>
            )}
          </TableHead>

          <TableBody>
            {visibleRecords.length > 0 ? (
              visibleRecords.map((row) => (
                <TableRow
                  hover
                  key={
                    row.missingPunchId ??
                    `${row.employeeId}-${row.missingDate}-${row.time}`
                  }
                  selected={selectedMissingPunchId === row.missingPunchId}
                >
                  <TableCell>{row.employeeId || "-"}</TableCell>
                  <TableCell>{formatEmployeeName(row.personName)}</TableCell>
                  <TableCell>{row.organization || "-"}</TableCell>
                  <TableCell>{formatDate(row.missingDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {normalizePunchFor(row.punchFor) === "In"
                        ? "Punch In Missing"
                        : normalizePunchFor(row.punchFor) === "Out"
                        ? "Punch Out Missing"
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.time || "-"}</TableCell>
                  <TableCell>{formatPunchType(row.punchType)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.attendanceStatus || "-"}
                      color={
                        String(row.attendanceStatus).toUpperCase() === "M"
                          ? "warning"
                          : "default"
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant={
                        selectedMissingPunchId === row.missingPunchId
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => onEdit(row)}
                      disabled={!row.missingPunchId}
                      sx={{ textTransform: "none", minWidth: 64 }}
                    >
                      {selectedMissingPunchId === row.missingPunchId
                        ? "Selected"
                        : "Edit"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <FeatherIcon icon="search" size={24} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    No records match the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[
                  5,
                  10,
                  25,
                  { label: "All", value: -1 },
                ]}
                colSpan={9}
                count={filteredRecords.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

MissingTable.propTypes = {
  missingPunchRecords: PropTypes.array,
  onEdit: PropTypes.func.isRequired,
  selectedMissingPunchId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

export default MissingTable;
